import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import './Tabs.scss';

/**
 * Hook to detect mobile viewport
 */
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);

  return isMobile;
};

const ChevronIcon = ({ isOpen }) => (
  <svg
    width="16" height="16" viewBox="0 0 16 16" fill="none"
    className={`custom-tabs-chevron ${isOpen ? 'open' : ''}`}
  >
    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * Custom Tabs Component with theme support
 * Matches UX_DESIGNS shadcn/ui tab styling
 * Responsive: auto-switches to dropdown on mobile for 4+ tabs
 */

export const Tabs = ({ defaultValue, value, onChange, children, className = '', ...props }) => {
  const [internalTab, setInternalTab] = useState(defaultValue);
  const activeTab = value !== undefined ? value : internalTab;
  const setActiveTab = useCallback((val) => {
    if (value === undefined) setInternalTab(val);
    if (onChange) onChange(val);
  }, [value, onChange]);

  const childrenArray = React.Children.toArray(children);
  const tabsList = childrenArray.find(child => child.type === TabsList);
  const tabsContents = childrenArray.filter(child => child.type === TabsContent);

  return (
    <div className={`custom-tabs ${className}`} {...props}>
      {tabsList && React.cloneElement(tabsList, { activeTab, setActiveTab })}
      {tabsContents.map(content => 
        React.cloneElement(content, { 
          key: content.props.value,
          isActive: content.props.value === activeTab 
        })
      )}
    </div>
  );
};

/**
 * Overflow tab rail — shows as many tabs as fit, collapsing the rest into a
 * "More ▾" dropdown. Recomputes on container resize. Opt-in via <TabsList overflow>.
 */
const OverflowTabsList = ({ tabs, activeTab, setActiveTab, className }) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const moreRef = useRef(null);
  const widthsRef = useRef([]);
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const [menuOpen, setMenuOpen] = useState(false);

  // Cache the natural width of each tab from a hidden measurement row.
  useLayoutEffect(() => {
    if (!measureRef.current) return;
    widthsRef.current = Array.from(measureRef.current.children).map(
      (n) => n.getBoundingClientRect().width
    );
  }, [tabs]);

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const widths = widthsRef.current;
    if (!container || widths.length === 0) return;
    const GAP = 4;        // matches the .25rem flex gap
    const MORE_W = 104;   // reserve space for the "More ▾" button
    const total = container.clientWidth;

    const sumAll = widths.reduce((a, w) => a + w + GAP, 0);
    if (sumAll <= total) { setVisibleCount(tabs.length); return; }

    let used = MORE_W;
    let count = 0;
    for (let i = 0; i < widths.length; i++) {
      used += widths[i] + GAP;
      if (used > total) break;
      count++;
    }
    setVisibleCount(Math.max(1, count));
  }, [tabs.length]);

  useLayoutEffect(() => { recompute(); }, [recompute]);
  useEffect(() => {
    const ro = new ResizeObserver(recompute);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [recompute]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const visible = tabs.slice(0, visibleCount);
  const overflowTabs = tabs.slice(visibleCount);
  const activeInOverflow = overflowTabs.some((t) => t.props.value === activeTab);
  const hasMore = overflowTabs.length > 0;

  return (
    <div
      ref={containerRef}
      className={`custom-tabs-list custom-tabs-list--overflow ${hasMore ? 'custom-tabs-list--has-more' : ''} ${className}`}
      role="tablist"
    >
      {visible.map((tab) =>
        React.cloneElement(tab, { activeTab, setActiveTab, key: tab.props.value })
      )}

      {hasMore && (
        <div className="custom-tabs-more" ref={moreRef}>
          <button
            type="button"
            className={`custom-tabs-trigger custom-tabs-more__btn ${activeInOverflow ? 'active' : ''}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            More <ChevronIcon isOpen={menuOpen} />
          </button>
          {menuOpen && (
            <div className="custom-tabs-more__menu" role="menu">
              {overflowTabs.map((tab) => (
                <button
                  key={tab.props.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={tab.props.value === activeTab}
                  className={`custom-tabs-dropdown-item ${tab.props.value === activeTab ? 'active' : ''}`}
                  onClick={() => { setActiveTab(tab.props.value); setMenuOpen(false); }}
                >
                  {tab.props.children}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hidden measurement row — never visible, used only to size tabs */}
      <div className="custom-tabs-measure" aria-hidden ref={measureRef}>
        {tabs.map((tab) => (
          <span key={tab.props.value} className="custom-tabs-trigger">{tab.props.children}</span>
        ))}
      </div>
    </div>
  );
};

export const TabsList = ({ children, className = '', activeTab, setActiveTab, mobileThreshold = 4, overflow = false }) => {
  const isMobile = useIsMobile();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const handleSelect = useCallback((value) => {
    setActiveTab(value);
    setDropdownOpen(false);
  }, [setActiveTab]);

  // Extract tab trigger children
  const tabs = React.Children.toArray(children).filter(
    child => child?.type === TabsTrigger
  );

  const activeLabel = tabs.find(tab => tab.props.value === activeTab)?.props.children || 'Select';

  // Dropdown mode: on mobile when tabs exceed threshold (default 3)
  if (isMobile && tabs.length > 3) {
    return (
      <div className={`custom-tabs-dropdown ${className}`} ref={dropdownRef}>
        <button
          type="button"
          className={`custom-tabs-dropdown-trigger ${dropdownOpen ? 'open' : ''}`}
          onClick={() => setDropdownOpen(prev => !prev)}
          aria-expanded={dropdownOpen}
          aria-haspopup="listbox"
        >
          <span className="custom-tabs-dropdown-label">{activeLabel}</span>
          <ChevronIcon isOpen={dropdownOpen} />
        </button>
        {dropdownOpen && (
          <div className="custom-tabs-dropdown-menu" role="menu">
            {tabs.map(tab => (
              <button
                key={tab.props.value}
                type="button"
                role="menuitemradio"
                aria-checked={tab.props.value === activeTab}
                className={`custom-tabs-dropdown-item ${tab.props.value === activeTab ? 'active' : ''}`}
                onClick={() => handleSelect(tab.props.value)}
              >
                {tab.props.children}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (overflow) {
    return (
      <OverflowTabsList
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        className={className}
      />
    );
  }

  return (
    <div className={`custom-tabs-list ${className}`} role="tablist">
      {React.Children.map(children, child =>
        child ? React.cloneElement(child, { activeTab, setActiveTab }) : null
      )}
    </div>
  );
};

export const TabsTrigger = ({ value, children, className = '', activeTab, setActiveTab }) => {
  const isActive = value === activeTab;
  
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      className={`custom-tabs-trigger ${isActive ? 'active' : ''} ${className}`}
      onClick={() => setActiveTab(value)}
    >
      {children}
    </button>
  );
};

export const TabsContent = ({ value, children, className = '', isActive }) => {
  if (!isActive) return null;
  
  return (
    <div
      role="tabpanel"
      className={`custom-tabs-content ${className}`}
    >
      {children}
    </div>
  );
};
